import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faGift, faTicket } from "@fortawesome/free-solid-svg-icons";
import styles from "./misBonosPage.module.css";
import { lotteryService } from "../../../../../services/api";
import { useBonuses } from "../../../../../context/bonusContext";
import { Badge, Button, EmptyState, Spinner } from "../../../../../components/ui";
import { Reveal } from "../../../../../components/motion";
import WelcomeBonusStrip from "../../../../../components/bonus/WelcomeBonusStrip";
import {
  bonusDaysLeftText,
  bonusLotteriesText,
  formatBonusLongDate,
} from "../../../../../utils/bonusRules";

const HISTORY_BADGE = {
  USED: { variant: "success", label: "Usado" },
  EXPIRED: { variant: "default", label: "Vencido" },
  CANCELLED: { variant: "error", label: "Cancelado" },
};

/**
 * "Mis bonos" en el perfil: bonos disponibles (boletos gratis) e historial
 * (usados, vencidos, cancelados) — GET /bonuses/mine/ vía BonusProvider.
 */
export default function MisBonosPage() {
  const navigate = useNavigate();
  const { available, history, loaded, loading, refresh } = useBonuses();
  const [betsById, setBetsById] = useState({});

  // Refrescar al abrir la sección (puede haber bonos nuevos por referidos o códigos).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Para los bonos usados: en qué apuesta (lotería y número) se usaron.
  const usedBetIds = useMemo(
    () => history.filter((b) => b.status === "USED" && b.bet_id).map((b) => String(b.bet_id)),
    [history]
  );
  const usedSignature = usedBetIds.join(",");

  useEffect(() => {
    if (!usedSignature) return undefined;
    let cancelled = false;
    lotteryService
      .getBets()
      .then((res) => {
        if (cancelled || !res.ok) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
        const map = {};
        list.forEach((bet) => {
          if (bet?.id) map[String(bet.id)] = bet;
        });
        setBetsById(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [usedSignature]);

  const goPlay = () => navigate("/apuestas");

  if (!loaded && loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Cargando tus bonos…" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <h2 className={styles.title}>Mis bonos</h2>
        {available.length > 0 && (
          <Button variant="primary" size="sm" onClick={goPlay}>
            Usar ahora
          </Button>
        )}
      </div>

      <p className={styles.intro}>
        Cada bono es una apuesta gratis: elige tu número en la lotería que quieras y, al pagar
        en el carrito, marca <strong>Usar mi bono</strong>. El bono de 1 fracción se usa en una
        apuesta de 1 fracción y el de billete en el billete completo.
      </p>

      <section aria-labelledby="bonos-disponibles">
        <h3 id="bonos-disponibles" className={styles.sectionTitle}>
          Disponibles
        </h3>
        {available.length === 0 ? (
          <div className={styles.emptyWrap}>
            <EmptyState
              icon={<FontAwesomeIcon icon={faGift} />}
              title="No tienes bonos disponibles"
              description="Gana bonos con tu primera recarga, invitando amigos o canjeando códigos promocionales."
            />
            <WelcomeBonusStrip variant="compact" />
          </div>
        ) : (
          <ul className={styles.list}>
            {available.map((bonus) => {
              const daysText = bonusDaysLeftText(bonus);
              const urgent = Number(bonus.days_left) <= 3;
              return (
                <Reveal as="li" key={bonus.id} className={`${styles.card} ${styles.cardAvailable}`}>
                  <span className={styles.cardIcon} aria-hidden="true">
                    <FontAwesomeIcon icon={bonus.kind === "TICKET" ? faTicket : faGift} />
                  </span>
                  <div className={styles.cardBody}>
                    <p className={styles.cardSource}>{bonus.source_display || "Bono"}</p>
                    <p className={styles.cardLabel}>{bonus.label}</p>
                    <p className={styles.cardMeta}>
                      <FontAwesomeIcon icon={faClock} /> Vence el{" "}
                      {formatBonusLongDate(bonus.expires_at)}
                      {daysText && (
                        <span className={urgent ? styles.daysUrgent : styles.days}>
                          {" "}
                          · {daysText}
                        </span>
                      )}
                    </p>
                    <p className={styles.cardMeta}>{bonusLotteriesText(bonus)}</p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={goPlay} className={styles.cardCta}>
                    Usar ahora
                  </Button>
                </Reveal>
              );
            })}
          </ul>
        )}
      </section>

      {history.length > 0 && (
        <section aria-labelledby="bonos-historial">
          <h3 id="bonos-historial" className={styles.sectionTitle}>
            Historial
          </h3>
          <ul className={styles.list}>
            {history.map((bonus) => {
              const badge = HISTORY_BADGE[bonus.status] || {
                variant: "default",
                label: bonus.status_display || bonus.status,
              };
              const bet = bonus.bet_id ? betsById[String(bonus.bet_id)] : null;
              let detail = "";
              if (bonus.status === "USED") {
                const when = bonus.used_at ? ` el ${formatBonusLongDate(bonus.used_at)}` : "";
                const where = bet
                  ? ` en ${bet.lottery_name || "tu apuesta"}${bet.number ? ` · Nº ${bet.number}` : ""}${
                      bet.series ? ` · Serie ${bet.series}` : ""
                    }`
                  : "";
                detail = `Usado${when}${where}.`;
              } else if (bonus.status === "EXPIRED") {
                detail = `Venció el ${formatBonusLongDate(bonus.expires_at)}.`;
              } else if (bonus.status === "CANCELLED") {
                detail = "Este bono fue cancelado.";
              }
              return (
                <li key={bonus.id} className={`${styles.card} ${styles.cardHistory}`}>
                  <span className={`${styles.cardIcon} ${styles.cardIconMuted}`} aria-hidden="true">
                    <FontAwesomeIcon icon={bonus.kind === "TICKET" ? faTicket : faGift} />
                  </span>
                  <div className={styles.cardBody}>
                    <p className={styles.cardSource}>{bonus.source_display || "Bono"}</p>
                    <p className={styles.cardLabel}>{bonus.label}</p>
                    {detail && <p className={styles.cardMeta}>{detail}</p>}
                  </div>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
